import React from "react";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { TIMELINE_FALLBACK } from "./timelineFallback";
import themeLarge from "../ThemeLarge";

// ─── layout constants ────────────────────────────────────────────────────────
const ROW_HEIGHT   = 460;          // total height of one serpentine row
const LINE_IN_ROW  = ROW_HEIGHT / 2; // 230 – the horizontal line sits here
const STEM_LENGTH  = 30;           // gap from the line to the nearest card edge
const CARD_WIDTH   = 200;          // info card width (px)
const H_PADDING    = 70;           // left/right padding (must be ≥ CURVE_RADIUS)
const CURVE_RADIUS = 60;           // how far the U-turn bulges out
const MIN_SLOT_W   = 220;          // minimum slot width per item

const MONTH_INDEX = {
  Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4,  Jun: 5,
  Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
};

class Timeline extends React.Component {
  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.state = {
      items: [],
      loading: true,
      isMobile: window.innerWidth <= 1024,
      containerWidth: window.innerWidth,
    };
  }

  componentDidMount() {
    window.addEventListener("resize", this.handleResize);
    this.loadTimeline();
  }

  componentDidUpdate(prevProps, prevState) {
    // Once items arrive, read the real container width from the DOM
    if (prevState.loading && !this.state.loading && this.containerRef.current) {
      const w = this.containerRef.current.offsetWidth;
      if (w !== this.state.containerWidth) this.setState({ containerWidth: w });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  handleResize = () => {
    this.setState({
      isMobile: window.innerWidth <= 1024,
      containerWidth: this.containerRef.current?.offsetWidth || window.innerWidth,
    });
  };

  loadTimeline = async () => {
    try {
      const db = getFirestore();
      const snap = await getDocs(collection(db, "timeline"));
      if (!snap.empty) {
        const items = snap.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            if (b.year !== a.year) return parseInt(b.year) - parseInt(a.year);
            return (MONTH_INDEX[b.month] ?? 0) - (MONTH_INDEX[a.month] ?? 0);
          });
        this.setState({ items, loading: false });
      } else {
        this.setState({ items: TIMELINE_FALLBACK, loading: false });
      }
    } catch {
      this.setState({ items: TIMELINE_FALLBACK, loading: false });
    }
  };

  // ─── layout helpers ──────────────────────────────────────────────────────

  buildRows(items, cw) {
    const usable = cw - H_PADDING * 2;
    const perRow = Math.max(2, Math.floor(usable / MIN_SLOT_W));
    const rows = [];
    for (let i = 0; i < items.length; i += perRow) {
      rows.push(items.slice(i, i + perRow));
    }
    return rows;
  }

  buildSvgPath(rows, cw) {
    const usable = cw - H_PADDING * 2;
    const leftX  = H_PADDING;
    const rightX = H_PADDING + usable;
    const segs   = [];

    rows.forEach((_, ri) => {
      const y      = ri * ROW_HEIGHT + LINE_IN_ROW;
      const isRTL  = ri % 2 === 1;
      const isLast = ri === rows.length - 1;

      if (ri === 0) segs.push(`M ${leftX},${y}`);

      if (!isRTL) {
        // left → right
        segs.push(`H ${rightX}`);
        if (!isLast) {
          const ny = (ri + 1) * ROW_HEIGHT + LINE_IN_ROW;
          segs.push(
            `C ${rightX + CURVE_RADIUS},${y} ${rightX + CURVE_RADIUS},${ny} ${rightX},${ny}`
          );
        }
      } else {
        // right → left
        segs.push(`H ${leftX}`);
        if (!isLast) {
          const ny = (ri + 1) * ROW_HEIGHT + LINE_IN_ROW;
          segs.push(
            `C ${leftX - CURVE_RADIUS},${y} ${leftX - CURVE_RADIUS},${ny} ${leftX},${ny}`
          );
        }
      }
    });

    return segs.join(" ");
  }

  buildPositions(rows, cw) {
    const usable  = cw - H_PADDING * 2;
    const positions = [];
    let gi = 0; // global index drives isTop alternation

    rows.forEach((row, ri) => {
      const y      = ri * ROW_HEIGHT + LINE_IN_ROW;
      const isRTL  = ri % 2 === 1;
      const slot   = usable / row.length;

      row.forEach((item, ii) => {
        const x = isRTL
          ? H_PADDING + usable - slot * ii - slot / 2
          : H_PADDING + slot * ii + slot / 2;

        positions.push({ item, x, y, isTop: gi % 2 !== 0 });
        gi++;
      });
    });

    return positions;
  }

  // ─── card content (shared by both layouts) ───────────────────────────────

  renderCardContent(item) {
    return (
      <>
        <span style={{
          fontSize: "1.6rem", fontWeight: "800",
          color: themeLarge.colors.primary,
          fontFamily: "Noto Sans", lineHeight: 1, display: "block",
        }}>
          {item.year}
        </span>
        <span style={{
          fontSize: themeLarge.fontSize.small, color: "#888",
          display: "block", marginBottom: "5px",
        }}>
          {item.month}
        </span>
        <span style={{
          fontSize: "0.9rem", fontWeight: "600",
          color: themeLarge.colors.text, display: "block", marginBottom: "3px",
        }}>
          {item.title}
        </span>
        <span style={{
          fontSize: "0.78rem", color: "#555",
          lineHeight: "1.5", display: "block",
        }}>
          {item.description}
        </span>
      </>
    );
  }

  // ─── desktop serpentine ──────────────────────────────────────────────────

  renderDesktop() {
    const { items, containerWidth: cw } = this.state;
    const rows       = this.buildRows(items, cw);
    const svgPath    = this.buildSvgPath(rows, cw);
    const positions  = this.buildPositions(rows, cw);
    const totalH     = rows.length * ROW_HEIGHT;

    return (
      <div
        ref={this.containerRef}
        style={{ position: "relative", width: "100%", height: `${totalH}px` }}
      >
        {/* ── SVG: path + stems + dots ── */}
        <svg
          width={cw}
          height={totalH}
          style={{ position: "absolute", top: 0, left: 0, overflow: "visible" }}
        >
          <path
            d={svgPath}
            stroke={themeLarge.colors.primary}
            strokeWidth="2"
            fill="none"
            opacity="0.55"
          />

          {positions.map(({ item, x, y, isTop }) => (
            <g key={item.id}>
              <line
                x1={x} y1={isTop ? y - STEM_LENGTH : y}
                x2={x} y2={isTop ? y              : y + STEM_LENGTH}
                stroke={themeLarge.colors.primary}
                strokeWidth="2"
                opacity="0.55"
              />
              <circle
                cx={x} cy={y} r="7"
                fill={themeLarge.colors.primary}
                stroke={themeLarge.colors.bg}
                strokeWidth="3"
              />
            </g>
          ))}
        </svg>

        {/* ── cards ── */}
        {positions.map(({ item, x, y, isTop }) => {
          const left = Math.max(4, Math.min(x - CARD_WIDTH / 2, cw - CARD_WIDTH - 4));
          return (
            <div
              key={item.id}
              style={{
                position: "absolute",
                width: `${CARD_WIDTH}px`,
                left: `${left}px`,
                padding: "10px 12px",
                background: themeLarge.colors.secondary,
                borderRadius: "10px",
                boxShadow: themeLarge.shadows.little,
                border: `1px solid ${themeLarge.colors.primary}40`,
                fontFamily: "Kumbh Sans",
                ...(isTop
                  ? { bottom: `${totalH - y + STEM_LENGTH}px` }
                  : { top: `${y + STEM_LENGTH}px` }),
              }}
            >
              {this.renderCardContent(item)}
            </div>
          );
        })}
      </div>
    );
  }

  // ─── mobile vertical ─────────────────────────────────────────────────────

  renderMobile() {
    const { items } = this.state;
    return (
      <div
        ref={this.containerRef}
        style={{ position: "relative", padding: "8px 16px 8px 48px" }}
      >
        <div style={{
          position: "absolute", left: "24px", top: 0, bottom: 0,
          width: "2px", background: themeLarge.colors.primary, opacity: 0.5,
        }} />

        {items.map((item) => (
          <div key={item.id} style={{ position: "relative", marginBottom: "24px" }}>
            <div style={{
              position: "absolute", left: "-31px", top: "8px",
              width: "14px", height: "14px", borderRadius: "50%",
              background: themeLarge.colors.primary,
              border: `3px solid ${themeLarge.colors.bg}`,
              zIndex: 1,
            }} />
            <div style={{
              background: themeLarge.colors.secondary,
              borderRadius: "10px", padding: "12px 16px",
              boxShadow: themeLarge.shadows.little,
              border: `1px solid ${themeLarge.colors.primary}40`,
              fontFamily: "Kumbh Sans",
            }}>
              {this.renderCardContent(item)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ─── render ──────────────────────────────────────────────────────────────

  render() {
    const { loading, items, isMobile } = this.state;
    if (loading || items.length === 0) return null;

    return (
      <div style={{
        background: themeLarge.colors.bg,
        padding: isMobile ? "24px 0" : "64px 0",
        overflow: "hidden",
      }}>
        {isMobile ? this.renderMobile() : this.renderDesktop()}
      </div>
    );
  }
}

export default Timeline;
